import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/InstagramCompareAccounts.graphql';
import { Query, QueryinstagramCompareAccountsArgs } from '../../__generated__/globalTypes';

export const useInstagramCompareAccountsQuery = (
  options?: QueryHookOptions<Query, QueryinstagramCompareAccountsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryinstagramCompareAccountsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useInstagramCompareAccountsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryinstagramCompareAccountsArgs>
): QueryResult<Query, QueryinstagramCompareAccountsArgs> & {
  getInstagramCompareAccounts: LazyQueryExecFunction<Query, QueryinstagramCompareAccountsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getInstagramCompareAccounts, result] = useLazyQuery<Query, QueryinstagramCompareAccountsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getInstagramCompareAccounts, ...result };
};

export const useInstagramCompareAccountsPromiseQuery = () => {
  const client = useApolloClient();

  const getInstagramCompareAccounts = useCallback(
    (options?: Omit<QueryOptions<QueryinstagramCompareAccountsArgs, Query>, 'query'>) =>
      client.query<Query, QueryinstagramCompareAccountsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getInstagramCompareAccounts };
};
// Suspense Query Hooks
export const useInstagramCompareAccountsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryinstagramCompareAccountsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryinstagramCompareAccountsArgs>(QUERY, {
    ...options
  });

  return result;
};
